import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl } from 'react-intl';
import { Button, Icon, Spinner } from '@salesforce/design-system-react';
import * as Util from '../utilities';

import './DuelingPicklist.scss';

/*
  Barebones implementation of lightning design system dueling pickling (https://www.lightningdesignsystem.com/components/dueling-picklist/)

  TODO:
   Keyboard Interactions
   Accessibility text

  NOTE:  I did look at pulling in someone else's attempt here (https://github.com/salesforce/design-system-react/pull/1712/files)
    Which may be more complete, but required serious modifications to allow for flexible styling.
*/

const LEFT = 0;
const RIGHT = 1;

export class DuelingPicklist extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeOptions: [],
      activePicklist: undefined
    };
  }

  componentDidUpdate(prevProps) {
    if (Util.propChanged(prevProps, this.props, 'isLoading', false)) {
      this.props.onSelect([...this.rightOptions()]);
    }
  }

  handleMove(fromPicklist) {
    if (!this.canMove(fromPicklist)) {
      return;
    }

    if (fromPicklist === LEFT) {
      const newSelectedTotal =
        this.rightOptions().length + this.activeOptions().length;

      if (newSelectedTotal > this.props.maxSelectable) {
        return;
      }
    }

    // selection is ALL of the items in the right picklist after the move
    const selectedOptions =
      fromPicklist === LEFT
        ? [...this.rightOptions(), ...this.activeOptions()]
        : [
            ...this.rightOptions().filter(
              opt => !this.state.activeOptions.includes(opt.id)
            )
          ];

    this.setState({
      activePicklist: undefined,
      activeOptions: []
    });

    this.props.onSelect(selectedOptions);
  }

  handleOptionClick(id) {
    // in which picklist is the item that was clicked?
    const picklist = this.leftOptions()
      .map(opt => opt.id)
      .includes(id)
      ? LEFT
      : RIGHT;

    // don't allow selection of locked options
    if (this.lockedOptionIds().includes(id)) {
      return;
    }

    // is it the active picklist?
    if (picklist === this.state.activePicklist) {
      //was it already active?
      const wasActive = this.state.activeOptions.includes(id);
      if (wasActive) {
        // remove it from being active
        this.setState({
          activeOptions: [...this.state.activeOptions.filter(opt => opt !== id)]
        });
      } else {
        // add it to active list
        this.setState({
          activeOptions: [...this.state.activeOptions, id]
        });
      }
    } else {
      // active picklist changed, unhighlight everything else
      this.setState({
        activeOptions: [id],
        activePicklist: picklist
      });
    }
  }

  leftOptions() {
    const selected = (this.props.selectedOptions || []).map(
      selected => selected.id
    );
    const options = this.props.options
      .filter(opt => !selected.includes(opt.id))
      .sort((a, b) => a.label.localeCompare(b.label));

    return this.props.arrangeLockedItemsFirst
      ? this.arrangeLockedItemsFirst(options)
      : options;
  }

  rightOptions() {
    const options = (this.props.selectedOptions || []).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    return this.props.arrangeLockedItemsFirst
      ? this.arrangeLockedItemsFirst(options)
      : options;
  }

  arrangeLockedItemsFirst(items) {
    const unlocked = items.filter(item => !item.locked);
    const locked = items.filter(item => item.locked);
    return [...locked, ...unlocked];
  }

  activeOptions() {
    return (this.props.options || []).filter(opt =>
      this.state.activeOptions.includes(opt.id)
    );
  }

  lockedOptionIds() {
    return (this.props.options || [])
      .filter(opt => opt.locked)
      .map(opt => opt.id);
  }

  isActive(id) {
    return this.state.activeOptions.includes(id);
  }

  canMove(fromPicklist) {
    // disable the arrow if we've exceeded the maximum selectable amount of options
    if (fromPicklist === LEFT) {
      // get the sum of everything that's already in the right list along with what's
      // been chosen in the left list.
      const total =
        this.rightOptions().length + this.state.activeOptions.length;

      if (
        total > this.props.maxSelectable ||
        this.rightOptions().length >= this.props.maxSelectable
      ) {
        return false;
      }
    }

    // otherwise return the result as usual
    return (
      this.state.activeOptions.length &&
      this.state.activePicklist === fromPicklist
    );
  }

  labelFor(node, count) {
    const { formatMessage } = this.props.intl;
    if (this.props.labels?.[node]) {
      return this.props.labels[node];
    }
    if (this.props.labelIds?.[node]) {
      return formatMessage({ id: this.props.labelIds[node] }, { count });
    }
  }

  renderPicklistItem(option) {
    const lockIcon = (
      <Icon
        assistiveText={{ label: 'Lock' }}
        category="utility"
        colorVariant="light"
        name="lock"
        size="x-small"
      />
    );
    return (
      <li
        key={option.id}
        data-id={option.id}
        role="row"
        className={`slds-listbox__item ${option.locked ? 'locked-item' : ''}`}
        onClick={() => this.handleOptionClick(option.id)}
      >
        <div
          className="slds-listbox__option slds-listbox__option_plain slds-media slds-media_small slds-media_inline"
          aria-selected={this.isActive(option.id)}
          draggable="true"
          role="option"
          tabIndex="0"
        >
          <span className="slds-media__body picklist-item">
            <span className="slds-truncate" title={option.id}>
              {option.label}
            </span>
            {option.locked ? lockIcon : null}
            {this.props.onRenderMenuItem
              ? this.props.onRenderMenuItem(option)
              : null}
          </span>
        </div>
      </li>
    );
  }

  render() {
    if (this.props.isLoading) {
      return (
        <div className="spinner">
          <Spinner
            size="large"
            variant="brand"
            assistiveText={{ label: 'Large spinner' }}
          />
        </div>
      );
    }

    // This solves the problem when a flagged attribute is selected in the dueling
    // pick list during profile import and attribute scan failed when editing the
    // import, the warnings will disappear if you move the attribute from the right
    // column to the left one. The reason is that the scan result on the right
    // hand side is from the cached attribute from the `attribute` endpoint but
    // the scan result on the left hand side is from a live scan from `column`
    // endpoint. In this case, moving an attribute from right to left means not
    // showing the attribute from the cached list but showing the attribute in
    // the non-cached list. Because the attr-scan service is down, the scan result
    // is ERROR in the non cached list and thus the warning sign disappears. This
    // is not a good solution so if you have a better one, feel free to upgrade this.
    if (this.props.options.some(o => o.scan_result?.status === 'ERROR')) {
      this.props.selectedOptions.forEach(i => (i.scan_result.status = 'ERROR'));
    }
    return (
      <div
        className="slds-form-element"
        role="group"
        aria-labelledby="picklist-group-label"
      >
        <span className="slds-form-element__label slds-form-element__legend profile-picklist-title">
          {this.labelFor('heading')}
        </span>
        <div className="slds-form-element__control attribute-picklist">
          <div className="slds-dueling-list">
            <div
              className="slds-assistive-text"
              id="drag-live-region"
              aria-live="assertive"
            />
            <div className="slds-assistive-text" id="option-drag-label" />
            <div className="slds-dueling-list__column">
              <span className="slds-form-element__label" id="label-107">
                {this.labelFor('left', this.leftOptions()?.length)}
              </span>
              <div className="slds-dueling-list__options">
                <ul
                  aria-describedby="option-drag-label"
                  aria-labelledby="label-107"
                  aria-multiselectable="true"
                  className="slds-listbox slds-listbox_vertical"
                  role="listbox"
                >
                  {this.leftOptions().map(option =>
                    this.renderPicklistItem(option)
                  )}
                </ul>
              </div>
            </div>
            <div className="slds-dueling-list__column">
              <Button
                onClick={() => this.handleMove(LEFT)}
                iconCategory="utility"
                iconName="right"
                iconVariant="container"
                variant="icon"
                assistiveText={{
                  icon: 'Move Selection to Right Category'
                }}
                disabled={!this.canMove(LEFT)}
              />
              <Button
                onClick={() => this.handleMove(RIGHT)}
                iconCategory="utility"
                iconName="left"
                iconVariant="container"
                variant="icon"
                assistiveText={{
                  icon: 'Move Selection to Left Category'
                }}
                disabled={!this.canMove(RIGHT)}
              />
            </div>
            <div className="slds-dueling-list__column">
              <span className="slds-form-element__label" id="label-108">
                {this.labelFor(
                  'right',
                  this.props.maxSelectable - this.rightOptions()?.length
                )}
              </span>
              <div className="slds-dueling-list__options">
                <ul
                  aria-describedby="option-drag-label"
                  aria-labelledby="label-108"
                  aria-multiselectable="true"
                  className="slds-listbox slds-listbox_vertical"
                  role="listbox"
                >
                  {this.rightOptions().map(option =>
                    this.renderPicklistItem(option)
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

DuelingPicklist.propTypes = {
  // All possible options on either side of the picklist
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      locked: PropTypes.bool,
      scan_result: PropTypes.shape({
        attribute: PropTypes.string,
        completed: PropTypes.bool,
        status: PropTypes.string.isRequired,
        warnings: PropTypes.arrayOf(
          PropTypes.shape({
            type: PropTypes.string.isRequired,
            message: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired
          })
        )
      })
    })
  ),

  // Selected options to be shown on the right picklist
  selectedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      locked: PropTypes.bool,
      scan_result: PropTypes.shape({
        attribute: PropTypes.string,
        completed: PropTypes.bool,
        status: PropTypes.string.isRequired,
        warnings: PropTypes.arrayOf(
          PropTypes.shape({
            type: PropTypes.string.isRequired,
            message: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired
          })
        )
      })
    })
  ),

  // actual labels to use -- takes precedent over labelIds
  labels: PropTypes.PropTypes.shape({
    heading: PropTypes.string,
    left: PropTypes.string,
    right: PropTypes.string
  }),

  // en.yml translation ids, if {count} is in the translation then it will be populated by the actual count of items
  labelIds: PropTypes.PropTypes.shape({
    heading: PropTypes.string,
    left: PropTypes.string,
    right: PropTypes.string
  }),

  // function that fires with each option being rendered and will append any react elements returned to the menu item
  onRenderMenuItem: PropTypes.func,

  arrangeLockedItemsFirst: PropTypes.bool,
  maxSelectable: PropTypes.number,
  onSelect: PropTypes.func,
  isLoading: PropTypes.bool,
  intl: intlShape.isRequired
};

export default injectIntl(DuelingPicklist);
